import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { fetchRoles, fetchRoleMembers, type Role } from '../services/roles';
import { TEAM_MEMBERS_KEY, TEAMS_LIST_KEY } from './queryKeys';
import { combineMembers } from './roleMembers';

const ROLE_MEMBERS_STALE_TIME = 2 * 60 * 1000;

export function useOdoRoles() {
    const { data, isLoading } = useQuery({
        queryKey: TEAMS_LIST_KEY('odo'),
        queryFn: () => fetchRoles('odo'),
        staleTime: 5 * 60 * 1000 // 5 minutes
    });

    const rolesByName = useMemo(() => {
        const map = new Map<string, Role>();
        for (const role of data ?? []) {
            map.set(role.name, role);
        }
        return map;
    }, [data]);

    return { roles: data ?? [], rolesByName, isLoading };
}

/** Members of the named odo roles, or of every odo role when `roles` is undefined. */
export function useAssignableMembers(roles?: readonly string[], enabled = true) {
    const { roles: odoRoles, rolesByName, isLoading: rolesLoading } = useOdoRoles();
    const roleIds = useMemo(() => {
        const ids =
            roles === undefined
                ? odoRoles.map((role) => role.id)
                : roles.flatMap((name) => rolesByName.get(name)?.id ?? []);
        return [...new Set(ids)].sort();
    }, [roles, odoRoles, rolesByName]);

    const { members, isLoading } = useQueries({
        queries: roleIds.map((roleId) => ({
            queryKey: TEAM_MEMBERS_KEY(roleId),
            queryFn: () => fetchRoleMembers(roleId),
            enabled,
            staleTime: ROLE_MEMBERS_STALE_TIME
        })),
        combine: combineMembers
    });

    return { members, isLoading: rolesLoading || isLoading };
}

/** A team role id as a picker's `roles`; a null or unknown id offers nobody. */
export function useRoleNamesForId(roleId: string | null): readonly string[] {
    const { roles } = useOdoRoles();
    const name = roles.find((role) => role.id === roleId)?.name;
    return useMemo(() => (name ? [name] : []), [name]);
}
